import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAntiFraudStore } from "../store/antiFraudStore";
import { useAIContext } from "../store/aiContextStore";
import { exportJSON } from "../utils/ExportJSON";
import { useToastStore } from "../store/toastStore";

/**
 * EU Anti-Fraud Interoperability Layer UI (v3)
 * 
 * Provides interfaces for:
 * - Risk scoring
 * - PEPPOL/eSeal verification
 * - UBO graph analysis
 * - Evidence attachment
 * - Interop reporting
 * - Audit log viewing
 */
export default function AntiFraud() {
  const { t } = useTranslation();
  const store = useAntiFraudStore();
  const showToast = useToastStore((state) => state.showToast);

  // Set active panel
  useEffect(() => {
    useAIContext.getState().setActivePanel("AntiFraudPanel");
  }, []);

  // Risk Scoring
  const [riskTx, setRiskTx] = useState({
    buyer: "",
    supplier: "",
    amount: 0,
    currency: "EUR",
    country: "",
    lotId: "",
    channel: "PORTAL" as const,
    meta: {} as Record<string, any>,
  });

  // PEPPOL Verification
  const [peppolDocId, setPeppolDocId] = useState("");
  const [peppolParticipantId, setPeppolParticipantId] = useState("");
  const [peppolSealHash, setPeppolSealHash] = useState("");

  // UBO Graph
  const [uboCompanyId, setUboCompanyId] = useState("");
  const [uboSource, setUboSource] = useState("");
  const [uboTarget, setUboTarget] = useState("");
  const [uboRelation, setUboRelation] = useState<"owns" | "controls" | "director" | "family" | "political">("owns");
  const [uboGraphList, setUboGraphList] = useState<Array<{ source: string; target: string; relation: typeof uboRelation }>>([]);

  // Evidence
  const [evidenceRef, setEvidenceRef] = useState("");
  const [evidenceHash, setEvidenceHash] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [evidenceKind, setEvidenceKind] = useState("");

  // Interop Report
  const [interopRef, setInteropRef] = useState("");
  const [interopKind, setInteropKind] = useState("");
  const [interopHashes, setInteropHashes] = useState("");

  const handleRiskScore = () => {
    const tx = {
      ...riskTx,
      lotId: riskTx.lotId || undefined,
      meta: Object.keys(riskTx.meta).length > 0 ? riskTx.meta : undefined,
    };
    store.computeRiskScore(tx);
  };

  const handlePeppolVerify = () => {
    store.verifyPeppol(peppolDocId, peppolParticipantId, peppolSealHash || undefined);
  };

  const handleAddUboEdge = () => {
    if (uboSource && uboTarget) {
      setUboGraphList([...uboGraphList, { source: uboSource, target: uboTarget, relation: uboRelation }]);
      setUboSource("");
      setUboTarget("");
    }
  };

  const handleSubmitUboGraph = () => {
    if (uboCompanyId && uboGraphList.length > 0) {
      store.submitUboGraph(uboCompanyId, uboGraphList);
    }
  };

  const handleAttachEvidence = () => {
    if (evidenceRef && (evidenceHash || evidenceUrl)) {
      store.attachEvidence(evidenceRef, [
        {
          hash: evidenceHash || undefined,
          url: evidenceUrl || undefined,
          kind: evidenceKind || undefined,
        },
      ]);
      setEvidenceRef("");
      setEvidenceHash("");
      setEvidenceUrl("");
      setEvidenceKind("");
    }
  };

  const handleReportInterop = () => {
    if (interopRef && interopKind) {
      const hashes = interopHashes.split(",").filter(Boolean);
      store.reportInterop(interopRef, interopKind, hashes.length > 0 ? hashes : undefined);
      setInteropRef("");
      setInteropKind("");
      setInteropHashes("");
    }
  };

  useEffect(() => {
    // Only fetch if API is available (backend running)
    const apiUrl = store.apiBaseUrl;
    fetch(`${apiUrl}/audit`, { method: "HEAD" })
      .then(() => {
        // API is available, fetch audit log
        store.fetchAuditLog();
      })
      .catch(() => {
        // API not available, skip fetch (backend not running)
        console.log("Anti-Fraud backend not available, skipping audit log fetch");
      });
  }, []); // Only check once on mount

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Risk Scoring Section */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("antifraud.riskScoring") || "Risk Scoring"}
          </h2>

          <div className="space-y-2">
            <input
              type="text"
              placeholder={t("antifraud.buyer") || "Buyer"}
              value={riskTx.buyer}
              onChange={(e) => setRiskTx({ ...riskTx, buyer: e.target.value })}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            />
            <input
              type="text"
              placeholder={t("antifraud.supplier") || "Supplier"}
              value={riskTx.supplier}
              onChange={(e) => setRiskTx({ ...riskTx, supplier: e.target.value })}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            />
            <input
              type="number"
              placeholder={t("antifraud.amount") || "Amount"}
              value={riskTx.amount || ""}
              onChange={(e) => setRiskTx({ ...riskTx, amount: Number(e.target.value) })}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            />
            <input
              type="text"
              placeholder={t("antifraud.country") || "Country (e.g., HU, BG)"}
              value={riskTx.country}
              onChange={(e) => setRiskTx({ ...riskTx, country: e.target.value })}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            />
            <select
              value={riskTx.channel}
              onChange={(e) => setRiskTx({ ...riskTx, channel: e.target.value as any })}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            >
              <option value="PEPPOL">PEPPOL</option>
              <option value="EMAIL">EMAIL</option>
              <option value="PORTAL">PORTAL</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>

          <button
            onClick={handleRiskScore}
            className="w-full bg-evat-neon text-black px-4 py-2 rounded font-orbitron hover:shadow-neon"
          >
            {t("antifraud.computeRisk") || "Compute Risk Score"}
          </button>

          {store.riskBand !== "LOW" && (
            <div className={`p-4 border rounded ${
              store.riskBand === "CRITICAL" ? "border-red-500 bg-red-900/20" :
              store.riskBand === "HIGH" ? "border-orange-500 bg-orange-900/20" :
              "border-yellow-500 bg-yellow-900/20"
            }`}>
              <div className="text-lg font-orbitron">
                {store.riskBand}: {store.riskScore}%
              </div>
            </div>
          )}
        </div>

        {/* PEPPOL/eSeal Verification */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("antifraud.peppolVerify") || "PEPPOL/eSeal Verification"}
          </h2>

          <input
            type="text"
            placeholder={t("antifraud.docId") || "Document ID"}
            value={peppolDocId}
            onChange={(e) => setPeppolDocId(e.target.value)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
          />
          <input
            type="text"
            placeholder={t("antifraud.participantId") || "Participant ID (0088:...)"}
            value={peppolParticipantId}
            onChange={(e) => setPeppolParticipantId(e.target.value)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
          />
          <input
            type="text"
            placeholder={t("antifraud.sealHash") || "Seal Hash (0x...)"}
            value={peppolSealHash}
            onChange={(e) => setPeppolSealHash(e.target.value)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
          />

          <button
            onClick={handlePeppolVerify}
            className="w-full bg-evat-gold/20 border border-evat-gold text-evat-gold px-4 py-2 rounded font-orbitron"
          >
            {t("antifraud.verify") || "Verify"}
          </button>

          {store.verification && (
            <div className={`p-4 border rounded ${
              store.peppolStatus === "VALID" ? "border-green-500 bg-green-900/20" : "border-red-500 bg-red-900/20"
            }`}>
              <div className="text-sm">Status: {store.peppolStatus}</div>
              <div className="text-sm">Seal Valid: {store.peppolSealValid ? "Yes" : "No"}</div>
              <div className="text-sm">PEPPOL OK: {store.peppolOk ? "Yes" : "No"}</div>
            </div>
          )}
        </div>

        {/* UBO Graph */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("antifraud.uboGraph") || "UBO Graph Analysis"}
          </h2>

          <input
            type="text"
            placeholder={t("antifraud.companyId") || "Company ID"}
            value={uboCompanyId}
            onChange={(e) => setUboCompanyId(e.target.value)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
          />

          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              placeholder={t("antifraud.source") || "Source"}
              value={uboSource}
              onChange={(e) => setUboSource(e.target.value)}
              className="bg-black border border-evat-gold/40 text-evat-neon rounded px-2 py-1"
            />
            <input
              type="text"
              placeholder={t("antifraud.target") || "Target"}
              value={uboTarget}
              onChange={(e) => setUboTarget(e.target.value)}
              className="bg-black border border-evat-gold/40 text-evat-neon rounded px-2 py-1"
            />
            <select
              value={uboRelation}
              onChange={(e) => setUboRelation(e.target.value as any)}
              className="bg-black border border-evat-gold/40 text-evat-neon rounded px-2 py-1"
            >
              <option value="owns">owns</option>
              <option value="controls">controls</option>
              <option value="director">director</option>
              <option value="family">family</option>
              <option value="political">political</option>
            </select>
          </div>

          <button
            onClick={handleAddUboEdge}
            className="w-full bg-evat-gold/20 border border-evat-gold text-evat-gold px-4 py-2 rounded font-orbitron text-sm"
          >
            {t("antifraud.addEdge") || "Add Edge"}
          </button>

          {uboGraphList.length > 0 && (
            <div className="text-sm text-neutral-400">
              {uboGraphList.length} edge(s) added
            </div>
          )}

          <button
            onClick={handleSubmitUboGraph}
            className="w-full bg-evat-neon text-black px-4 py-2 rounded font-orbitron"
            disabled={!uboCompanyId || uboGraphList.length === 0}
          >
            {t("antifraud.submitGraph") || "Submit UBO Graph"}
          </button>

          {store.uboHints.length > 0 && (
            <div className="p-4 border border-yellow-500/40 bg-yellow-900/20 rounded">
              <div className="text-sm font-orbitron mb-2">Hints:</div>
              {store.uboHints.map((h, i) => (
                <div key={i} className="text-xs text-yellow-300">• {h}</div>
              ))}
            </div>
          )}
        </div>

        {/* Evidence & Interop */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("antifraud.evidenceInterop") || "Evidence & Interop"}
          </h2>

          <div>
            <div className="text-sm text-neutral-400 mb-2">{t("antifraud.attachEvidence") || "Attach Evidence"}</div>
            <input
              type="text"
              placeholder={t("antifraud.ref") || "Reference"}
              value={evidenceRef}
              onChange={(e) => setEvidenceRef(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 mb-2"
            />
            <input
              type="text"
              placeholder={t("antifraud.hash") || "Hash (0x...)"}
              value={evidenceHash}
              onChange={(e) => setEvidenceHash(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 mb-2"
            />
            <input
              type="text"
              placeholder={t("antifraud.url") || "URL"}
              value={evidenceUrl}
              onChange={(e) => setEvidenceUrl(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 mb-2"
            />
            <button
              onClick={handleAttachEvidence}
              className="w-full bg-evat-gold/20 border border-evat-gold text-evat-gold px-4 py-2 rounded font-orbitron text-sm"
            >
              {t("antifraud.attach") || "Attach"}
            </button>
          </div>

          <div className="border-t border-evat-gold/40 pt-4 mt-4">
            <div className="text-sm text-neutral-400 mb-2">{t("antifraud.reportInterop") || "Report Interop"}</div>
            <input
              type="text"
              placeholder={t("antifraud.ref") || "Reference"}
              value={interopRef}
              onChange={(e) => setInteropRef(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 mb-2"
            />
            <input
              type="text"
              placeholder={t("antifraud.kind") || "Kind"}
              value={interopKind}
              onChange={(e) => setInteropKind(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 mb-2"
            />
            <input
              type="text"
              placeholder={t("antifraud.hashes") || "Hashes (comma-separated)"}
              value={interopHashes}
              onChange={(e) => setInteropHashes(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 mb-2"
            />
            <button
              onClick={handleReportInterop}
              className="w-full bg-evat-neon text-black px-4 py-2 rounded font-orbitron text-sm"
            >
              {t("antifraud.report") || "Report"}
            </button>
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div className="p-6 border border-evat-gold/40 rounded-lg bg-black/50">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-orbitron text-evat-neon">
            {t("antifraud.auditLog") || "Audit Log"}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => store.fetchAuditLog()}
              className="bg-evat-gold/20 border border-evat-gold text-evat-gold px-4 py-2 rounded font-orbitron text-sm hover:bg-evat-gold/30 transition-colors"
            >
              {t("antifraud.refresh") || "Refresh"}
            </button>
            <button
              onClick={() => {
                try {
                  const data = {
                    version: "1.0",
                    exportedAt: new Date().toISOString(),
                    auditLog: store.auditLog,
                  };
                  const filename = `antifraud_audit_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.json`;
                  exportJSON(filename, data);
                  showToast(t("antifraud.exportSuccess") || "Audit log exported successfully", "success");
                } catch (e) {
                  showToast(t("antifraud.exportFailed") || "Failed to export audit log", "error");
                }
              }}
              className="bg-evat-neon/20 border border-evat-neon text-evat-neon px-4 py-2 rounded font-orbitron text-sm hover:bg-evat-neon/30 transition-colors"
            >
              {t("antifraud.export") || "Export"}
            </button>
          </div>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {store.auditLog.slice().reverse().map((entry) => (
            <div key={entry.id} className="p-3 border border-evat-gold/20 rounded text-sm">
              <div className="flex justify-between">
                <span className="text-evat-neon">{entry.type}</span>
                <span className="text-neutral-500">
                  {new Date(entry.ts).toLocaleString()}
                </span>
              </div>
              <pre className="text-xs text-neutral-400 mt-2 overflow-x-auto">
                {JSON.stringify(entry, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

